'use client';
import Link from 'next/link';
import MobileMenu from './MobileMenu';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Navlinks } from '@/data/data';

export default function Navbar() {
  const pathname = usePathname();
  const router = useRouter();
  const [activeNav, setActiveNav] = useState<string>(pathname);

  useEffect(() => {
    // update on route change
    setActiveNav(pathname);
  }, [pathname]);

  useEffect(() => {
    // handle scroll for hash sections on homepage
    if (pathname === '/') {
      const handleScroll = () => {
        let current = 'home';
        Navlinks.forEach((link) => {
          if (link.href.startsWith('#')) {
            const id = link.href.slice(1);
            const section = document.getElementById(id);
            if (section) {
              const rect = section.getBoundingClientRect();

              // adjust for navbar height (80px)
              if (rect.top <= 100) {
                current = link.href;
              }
            }
          }
        });
        setActiveNav(current);
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [pathname]);

  return (
    <nav className="w-full h-[80px] bg-[#141f20] shadow-md sticky top-0 left-0 z-[4]">
      <div className="NavWrapper flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="text-2xl font-bold text-white">
          <Image
            src="/icon.png"
            alt="Evonix Logo"
            width={120}
            height={40}
            className="object-contain"
            priority
          />
        </Link>

        {/* Desktop Links */}
        <ul className="hidden md:flex gap-8 relative">
          {Navlinks.map((e) => (
            <li key={e.name} className="relative group">
              {e.href.startsWith('#') ? (
                // Hash links → smooth scroll
                <a
                  href={e.href}
                  className={`text-white font-medium transition-colors ${activeNav === e.href ? 'text-yellow-400' : 'hover:text-yellow-500'}`}
                  onClick={(ev) => {
                    ev.preventDefault();
                    const targetId = e.href.slice(1);

                    if (pathname === '/') {
                      const targetElement = document.getElementById(targetId);
                      if (targetElement) {
                        targetElement.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                        });
                      }
                    } else {
                      router.push('/' + e.href);
                    }
                  }}
                >
                  {e.name}
                  <span
                    className={`absolute bottom-[-4px] left-0 right-0 h-[2px] origin-left rounded-full bg-yellow-400 
                    transition-transform duration-300 ease-out
                    ${activeNav === e.href ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}
                  />
                </a>
              ) : (
                // Page links → Next.js Link
                <Link
                  href={e.href}
                  className={`text-white font-medium transition-colors ${activeNav === e.href ? 'text-yellow-400' : 'hover:text-yellow-500'}`}
                >
                  {e.name}
                  <span
                    className={`absolute bottom-[-4px] left-0 right-0 h-[2px] origin-left rounded-full bg-yellow-400 
                    transition-transform duration-300 ease-out
                    ${activeNav === e.href ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}
                  />
                </Link>
              )}
            </li>
          ))}
        </ul>

        {/* Mobile Menu */}
        <div className="md:hidden">
          <MobileMenu
            links={Navlinks}
            activeNav={activeNav}
            setActiveNav={setActiveNav}
            pathname={pathname}
          />
        </div>
      </div>
    </nav>
  );
}
